import React, { useEffect, useState, useCallback, useContext } from "react";
import { handleError } from "services/util/ApiUtil";
import { Container, Row, Col } from "reactstrap";
import { LoadingIndicator } from "components/framework/loadingIndicator/LoadingIndicator";
import { DefaultOrder, OrderDto } from "services/apis/types/order/OrderDto";
import PortingNumbersTable from "./PortingNumbersTable";
import { useIsMounted } from "services/customHooks/useIsMounted";
import { OrderApi } from "services/apis/OrderApi";
import OrderMetadataCard from "./OrderMetadataCard";
import OrderPortTypeCard from "./OrderPortTypeCard";
import OrderStatusCard from "./OrderStatusCard";
import NotesCard from "./notes/NotesCard";
import TasksCard from "./tasks/TasksCard";
import OrderDetailsCard from "./OrderDetailsCard";
import { AppContext } from "services/appContext/AppContext";
import { EnvironmentUtil } from "services/util/EnvironmentUtil";
import { PermissionType } from "services/authorization/PermissionType";
import { SinchApi } from "services/apis/SinchApi";
import { StatusType } from "services/apis/types/order/NumberStatus";
import { NoteDto } from "services/apis/types/order/NoteDto";
import { useControlDropdown } from "services/customHooks/useDropdownDisabled";

type OrderDetailsContext = {
  fetchOrder: () => void;
  order: OrderDto;
};

export const OrderDetailsContext = React.createContext<OrderDetailsContext>({
  fetchOrder: () => {},
  order: DefaultOrder
});

const isMixNetworkUrl = EnvironmentUtil.isMixNetwork;

type Props = {
  orderId: string;
  isDisasterRecovery?: boolean;
  isSinch?: boolean;
  onOrderFetched: (order: OrderDto) => void;
};

export default function OrderDetailsContainer(props: Props) {
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(true);
  const [order, setOrder] = useState<OrderDto>(DefaultOrder);
  const isMounted = useIsMounted();
  const { appContext } = useContext(AppContext);
  const isUserAdmin = appContext.localStorageInfo.user?.permissions.some(value => value === PermissionType.Admin);
  const isUserCompanyAdmin = appContext.localStorageInfo.user?.permissions.some(value => value === PermissionType.CompanyAdmin);
  const showPortTypeCard = !isMixNetworkUrl || isUserAdmin || isUserCompanyAdmin;
  useControlDropdown();

useEffect(() => {
  if (props.orderId) {
    fetchOrder();
  }
}, [props.orderId]);

  const fetchOrder = useCallback(() => {
    if (props.isDisasterRecovery) {
      OrderApi.getDR(props.orderId)
      .then((result) => {
        if (isMounted) {
          setOrder(result);
          props.onOrderFetched(result);
        }
      })
      .catch((error) => handleError(error))
      .finally(() => {
        if (isMounted) {
          setShowLoadingIndicator(false);
        }
      });
    } 
    else if (props.isSinch) {
      SinchApi.getSinch(props.orderId)
        .then((result) => {
          if (isMounted) {
            setOrder(result);
            props.onOrderFetched(result);
          }
        })
        .catch((error) => handleError(error))
        .finally(() => {
          if (isMounted) {
            setShowLoadingIndicator(false);
          }
        });
    }
    else {
      OrderApi.get(props.orderId)
      .then((result) => {
        if (isMounted) {
          setOrder(result);
          props.onOrderFetched(result);
        }
      })
      .catch((error) => handleError(error))
      .finally(() => {
        if (isMounted) {
          setShowLoadingIndicator(false);
        }
      });
    }
  }, [props.orderId, isMounted, props.isDisasterRecovery, props.isSinch]);

  useEffect(() => {
    if (props.orderId) {
      fetchOrder();
    }
  }, [props.orderId, fetchOrder]);

  const orderDetailsContext: OrderDetailsContext = {
    fetchOrder: fetchOrder,
    order: order
  };

  return (
    <OrderDetailsContext.Provider value={orderDetailsContext}>
      <Container fluid>
        {showLoadingIndicator ? (
          <LoadingIndicator white />
        ) : (
          <>
            <Row>
              <Col xl={showPortTypeCard ? "5" : "6"} md="12" className="d-flex">
                <OrderMetadataCard orderMetadata={order.metadata} orderId={order.orderId} orderType={order.orderType} highPriority={order?.isHighPriority} status={order?.status} />
              </Col>
              {showPortTypeCard && (
                <Col xl="3" md="6">
                  <OrderPortTypeCard
                    order={order}
                    orderType={order.orderType}
                    loosingSpId={order.loosingProvider}
                    gainingSpId={order.gainingProvider}
                  />
                </Col>
              )}
              <Col xl={showPortTypeCard ? "4" : "6"} md="12" className="d-flex">
            <OrderStatusCard order={order}/>
              </Col>
            </Row>
            {order.customerDetails && !props.isDisasterRecovery && !props.isSinch && (
              <Row>
                <Col md="12">
                  <OrderDetailsCard order={order} />
                </Col>
              </Row>
            )}
            <Row>
              <Col md="5" className="mb-3">
                <PortingNumbersTable order={order} isDisasterRecovery={props.isDisasterRecovery} isSinch={props.isSinch} />
              </Col>
              {!props.isSinch && <>
                <Col md="3">
                  <TasksCard orderId={props.orderId} />
                </Col>
                <Col md="4">
                  <NotesCard order={order} />
                </Col>
              </>}
            </Row>
          </>
        )}
      </Container>
    </OrderDetailsContext.Provider>
  );
}
